// VimMode.js
//
// The page already looks like vim, so it may as well answer to it. Press `i`
// to edit the buffer, `:` to open the command line. Nothing here persists: `:w`
// exists to say so.

const MESSAGES = {
  noWrite: "E37: No write since last change (add ! to override)",
  readonly: "E212: Can't open file for writing: Permission denied (are you root?)",
  notCommand: (cmd) => `E492: Not an editor command: ${cmd}`,
  noFile: (name) => `E484: Can't open file ${name}`,
};

// `i` should reach a link or a button the visitor is tabbing through rather
// than opening the editor under their feet.
const INTERACTIVE = "a, button";

export class VimMode {
  constructor({ element, buffers = {} }) {
    this.element = element;
    this.content = element.querySelector(".window-content");
    this.title = element.querySelector(".window-top .title");
    this.file = element.querySelector(".window-shell .file");
    this.modeChip = element.querySelector(".window-shell .mode");
    this.cmdline = element.querySelector(".window-shell .cmdline");
    this.buffers = buffers;

    this.mode = "normal";
    this.command = "";
    this.modified = false;

    this.content.addEventListener("input", () => {
      this.modified = true;
      this.renderTitle();
    });
    document.addEventListener("keydown", this.keyDown.bind(this));
  }

  keyDown(e) {
    // Leave the browser's own shortcuts alone.
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }

    if (this.mode === "insert") {
      if (e.key === "Escape") {
        e.preventDefault();
        this.toNormal();
      }
      return; // everything else is typing
    }

    if (this.mode === "command") {
      this.commandKey(e);
      return;
    }

    if (this.isClosed()) {
      return;
    }

    if (e.key === "i" && !this.onInteractive()) {
      e.preventDefault();
      this.toInsert();
      return;
    }

    if (e.key === ":") {
      e.preventDefault();
      this.toCommand();
    }
  }

  commandKey(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      this.toNormal();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      this.run(this.command.trim());
      return;
    }

    if (e.key === "Backspace") {
      e.preventDefault();
      // Backspacing past the colon leaves the command line, as it does in vim.
      if (this.command === "") {
        this.toNormal();
      } else {
        this.command = this.command.slice(0, -1);
        this.renderCommand();
      }
      return;
    }

    if (e.key.length === 1) {
      e.preventDefault();
      this.command += e.key;
      this.renderCommand();
    }
  }

  run(command) {
    if (command === "") {
      this.toNormal();
      return;
    }

    if (command === "q") {
      // Always, even on an untouched buffer: the point is the joke, and :q!
      // is right there.
      this.message(MESSAGES.noWrite);
      return;
    }

    if (command === "q!") {
      this.quit();
      return;
    }

    if (command === "w" || command === "wq" || command === "x") {
      this.message(MESSAGES.readonly);
      return;
    }

    const edit = command.match(/^e!?\s+(\S+)$/);
    if (edit) {
      this.edit(edit[1]);
      return;
    }

    this.message(MESSAGES.notCommand(command));
  }

  edit(name) {
    // `:e README`, `:e README.txt` and `:e readme` all mean the same buffer.
    const key = name.toLowerCase().replace(/\.[^.]+$/, "");
    const buffer = this.buffers[key];

    if (!buffer) {
      this.message(MESSAGES.noFile(name));
      return;
    }

    this.toNormal();
    this.modified = false;
    buffer();
  }

  quit() {
    this.toNormal();
    this.element.classList.add("is-closed");
  }

  toInsert() {
    this.mode = "insert";
    this.editable(true);
    this.render();
  }

  toNormal() {
    this.mode = "normal";
    this.command = "";
    this.editable(false);
    this.render();
  }

  toCommand() {
    this.mode = "command";
    this.command = "";
    this.render();
  }

  message(text) {
    this.mode = "normal";
    this.command = "";
    this.editable(false);
    this.render();
    this.cmdline.textContent = text;
    this.cmdline.classList.add("is-error");
    this.cmdline.hidden = false;
    this.file.hidden = true;
  }

  render() {
    this.cmdline.classList.remove("is-error");
    this.renderTitle();

    if (this.mode === "command") {
      this.renderCommand();
      return;
    }

    this.cmdline.hidden = true;
    this.file.hidden = false;
    this.modeChip.hidden = false;
    this.modeChip.textContent = this.mode === "insert" ? "-- INSERT --" : "-- NORMAL --";
  }

  renderCommand() {
    this.cmdline.textContent = `:${this.command}`;
    this.cmdline.hidden = false;
    this.file.hidden = true;
    this.modeChip.hidden = true;
  }

  renderTitle() {
    const buffer = this.title.dataset.buffer;
    if (buffer) {
      this.title.textContent = `■ ${buffer}${this.modified ? " [+]" : ""}`;
    }
  }

  // Leaving insert has to hand the focus back as well as the attribute:
  // a still-focused content area keeps the :focus-visible ring, which the
  // window then clips into a stray line down its edge.
  editable(on) {
    this.content.setAttribute("contenteditable", on ? "true" : "false");
    if (on) {
      this.content.focus();
    } else {
      this.content.blur();
    }
  }

  isClosed() {
    return this.element.classList.contains("is-closed");
  }

  onInteractive() {
    const focused = document.activeElement;
    return Boolean(focused && focused.matches && focused.matches(INTERACTIVE));
  }
}
